import { CalendarTodayOutlined, CheckOutlined, EditOutlined } from '@mui/icons-material';
import { MIXTURE_PRODUCT_COLOR_COUNT } from '../../constants/mixtures.constants';
import type { MaintenanceMixture } from '../../types';
import {
	formatMixtureDate,
	formatMixtureDose,
	type MixtureUsage,
} from '../../utils/mixturesMapper';
import { MixtureStatusBadge } from './MixtureStatusBadge';
import { MixtureTank } from './MixtureTank';
import { MixtureUsageSummary } from './MixtureUsageSummary';
import styles from './MixtureCard.module.css';

interface MixtureCardProps {
	mixture: MaintenanceMixture;
	position: number;
	usage: MixtureUsage;
	sectorName?: string;
	isEditing?: boolean;
	onEdit?: () => void;
	onConfirm?: () => void;
}

const PRODUCT_COLOR_CLASSES = [styles.product1, styles.product2, styles.product3];

export const MixtureCard = ({
	mixture,
	position,
	usage,
	sectorName,
	isEditing,
	onEdit,
	onConfirm,
}: MixtureCardProps) => {
	const isConfirmed = mixture.status === 'confirmed';
	const hasActions = !!(onEdit || onConfirm);

	return (
		<article className={`${styles.card} ${isEditing ? styles.editing : ''}`}>
			<div className={styles.tankColumn}>
				<MixtureTank filled />
				<span className={styles.tankPercent}>{usage.percentage}%</span>
			</div>
			<div className={styles.content}>
				<header className={styles.header}>
					<h4 className={styles.title}>Mistura {position}</h4>
					{sectorName && <span className={styles.sector}>{sectorName}</span>}
					<span className={styles.date}>
						<CalendarTodayOutlined className={styles.dateIcon} />
						{formatMixtureDate(mixture.date)}
					</span>
					<MixtureStatusBadge mixture={mixture} />
				</header>
				<ul className={styles.products}>
					{mixture.products.map((product, index) => (
						<li key={`${product.type}-${product.id}`} className={styles.product}>
							<span
								className={`${styles.productBar} ${PRODUCT_COLOR_CLASSES[index % MIXTURE_PRODUCT_COLOR_COUNT]}`}
							/>
							<span className={styles.productName}>{product.name ?? 'Produto'}</span>
							<span className={styles.productDose}>{formatMixtureDose(product)}</span>
						</li>
					))}
				</ul>
				<footer className={styles.footer}>
					<MixtureUsageSummary usage={usage} />
					{hasActions && (
						<div className={styles.actions}>
							{onEdit && !isConfirmed && (
								<button
									type="button"
									className={`${styles.button} ${styles.outline} ${styles.medium}`}
									onClick={onEdit}
								>
									<EditOutlined className={styles.buttonIcon} />
									Editar
								</button>
							)}
							{!isConfirmed && onConfirm && (
								<button
									type="button"
									className={`${styles.button} ${styles.primary}`}
									onClick={onConfirm}
								>
									<CheckOutlined className={styles.buttonIcon} />
									Confirmar
								</button>
							)}
						</div>
					)}
				</footer>
			</div>
		</article>
	);
};
